import { useMemo, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GitCompare, TrendingUp, TrendingDown } from "lucide-react";
import { useListTermSheetVersions, getListTermSheetVersionsQueryKey, type TermSheetVersion, type TermSheetVersionActor } from "@workspace/api-client-react";
import { format } from "date-fns";

function actorInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function actorDisplayName(actor: TermSheetVersionActor | null | undefined): string {
  return actor?.name?.trim() || "Unknown";
}

function actorTooltipText(actor: TermSheetVersionActor | null | undefined): string {
  if (!actor) return "Actor unknown";
  const bits: string[] = [actorDisplayName(actor)];
  if (actor.role) bits.push(actor.role);
  if (actor.personas && actor.personas.length > 0) bits.push(actor.personas.join(", "));
  return bits.join(" · ");
}

function ActorPill({ actor, size = "sm" }: { actor: TermSheetVersionActor | null | undefined; size?: "sm" | "xs" }) {
  const px = size === "xs" ? "h-4 w-4 text-[9px]" : "h-5 w-5 text-[10px]";
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 align-middle" data-testid="ts-version-actor">
            <Avatar className={px}>
              {actor?.imageUrl ? <AvatarImage src={actor.imageUrl} alt={actorDisplayName(actor)} /> : null}
              <AvatarFallback className="text-[9px]">{actorInitials(actor?.name)}</AvatarFallback>
            </Avatar>
            <span className="text-xs">{actorDisplayName(actor)}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{actorTooltipText(actor)}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return "$" + n.toLocaleString();
}

function DeltaBadge({ base, compare }: { base: number | null | undefined; compare: number | null | undefined }) {
  if (base == null || compare == null) return null;
  const delta = compare - base;
  if (Math.abs(delta) < 1) return <span className="text-xs text-muted-foreground ml-1">no change</span>;
  const pct = base !== 0 ? ((delta / base) * 100).toFixed(1) : "—";
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-green-700 font-medium ml-1">
        <TrendingUp className="h-3 w-3" /> +{fmtMoney(delta)} (+{pct}%)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-red-700 font-medium ml-1">
      <TrendingDown className="h-3 w-3" /> {fmtMoney(delta)} ({pct}%)
    </span>
  );
}

function NumericRow({
  label,
  base,
  compare,
}: {
  label: string;
  base: number | null | undefined;
  compare: number | null | undefined;
}) {
  const differs = base != null && compare != null && Math.abs(base - compare) >= 1;
  return (
    <div
      className={`grid grid-cols-[1fr_1fr_1fr] gap-2 py-2.5 text-sm border-b last:border-0 ${differs ? "bg-amber-50/40" : ""}`}
    >
      <div className="text-muted-foreground font-medium pl-2">{label}</div>
      <div className="text-center font-mono">{fmtMoney(base ?? null)}</div>
      <div className={`text-center font-mono ${differs ? "font-semibold text-foreground" : ""}`}>
        {fmtMoney(compare ?? null)}
        <DeltaBadge base={base ?? null} compare={compare ?? null} />
      </div>
    </div>
  );
}

function TextRow({
  label,
  base,
  compare,
}: {
  label: string;
  base: string | null | undefined;
  compare: string | null | undefined;
}) {
  const differs = (base ?? "") !== (compare ?? "");
  return (
    <div
      className={`grid grid-cols-[1fr_1fr_1fr] gap-2 py-2.5 text-sm border-b last:border-0 ${differs ? "bg-amber-50/40" : ""}`}
    >
      <div className="text-muted-foreground font-medium pl-2">{label}</div>
      <div className="text-xs whitespace-pre-wrap">{base || <span className="italic text-muted-foreground">—</span>}</div>
      <div className={`text-xs whitespace-pre-wrap ${differs ? "font-semibold text-foreground" : ""}`}>
        {compare || <span className="italic text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function versionLabel(v: TermSheetVersion): string {
  const sourceLabel = v.source === "internal" ? "Sent" : v.source === "accepted" ? "Accepted" : "Counter";
  const dt = v.createdAt ? format(new Date(v.createdAt), "MMM d, h:mm a") : "";
  const actorName = v.actor?.name?.trim();
  const who = actorName ? ` · ${actorName}` : "";
  return `v${v.versionNumber} · ${sourceLabel}${who}${dt ? ` · ${dt}` : ""}`;
}

interface TermSheetCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termSheetId: number;
}

export function TermSheetCompareDialog({ open, onOpenChange, termSheetId }: TermSheetCompareDialogProps) {
  const { data: versions = [], isLoading } = useListTermSheetVersions(termSheetId, {
    query: { enabled: open, queryKey: getListTermSheetVersionsQueryKey(termSheetId) },
  });

  const sorted = useMemo(
    () => [...versions].sort((a, b) => a.versionNumber - b.versionNumber),
    [versions],
  );

  const [baseId, setBaseId] = useState<string>("");
  const [compareId, setCompareId] = useState<string>("");

  // Default to first vs last (or last two) once loaded.
  useEffect(() => {
    if (!open || sorted.length === 0) return;
    if (sorted.length === 1) {
      setBaseId(String(sorted[0].id));
      setCompareId(String(sorted[0].id));
      return;
    }
    setBaseId(String(sorted[sorted.length - 2].id));
    setCompareId(String(sorted[sorted.length - 1].id));
  }, [open, sorted]);

  const base = sorted.find((v) => String(v.id) === baseId);
  const compare = sorted.find((v) => String(v.id) === compareId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" data-testid="dialog-ts-compare">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Compare Term Sheet Versions
          </DialogTitle>
          <DialogDescription>
            Side-by-side diff of any two snapshots. Internal sends and received counters are both captured automatically.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading versions…</div>
        ) : sorted.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            No versions yet. Send the term sheet or record a counter to begin tracking versions.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Base</div>
                <Select value={baseId} onValueChange={setBaseId}>
                  <SelectTrigger data-testid="select-ts-compare-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sorted.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>{versionLabel(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Compare</div>
                <Select value={compareId} onValueChange={setCompareId}>
                  <SelectTrigger data-testid="select-ts-compare-compare">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sorted.map((v) => (
                      <SelectItem key={v.id} value={String(v.id)}>{versionLabel(v)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {base && compare && (
              <div className="border rounded-md mt-3 overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_1fr] gap-2 py-2 px-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <div>Field</div>
                  <div className="text-center space-y-1">
                    <div>
                      <Badge variant="outline">{base.source === "internal" ? "Sent" : base.source === "accepted" ? "Accepted" : "Counter"}</Badge>{" "}
                      v{base.versionNumber}
                    </div>
                    <div className="flex justify-center"><ActorPill actor={base.actor} /></div>
                  </div>
                  <div className="text-center space-y-1">
                    <div>
                      <Badge variant="outline">{compare.source === "internal" ? "Sent" : compare.source === "accepted" ? "Accepted" : "Counter"}</Badge>{" "}
                      v{compare.versionNumber}
                    </div>
                    <div className="flex justify-center"><ActorPill actor={compare.actor} /></div>
                  </div>
                </div>

                <TextRow label="Status" base={base.snapshot.status ?? null} compare={compare.snapshot.status ?? null} />
                <NumericRow label="Salary range min" base={base.snapshot.salaryRangeMin} compare={compare.snapshot.salaryRangeMin} />
                <NumericRow label="Salary range max" base={base.snapshot.salaryRangeMax} compare={compare.snapshot.salaryRangeMax} />
                <NumericRow label="Signing bonus" base={base.snapshot.signingBonus} compare={compare.snapshot.signingBonus} />
                <NumericRow label="Annual salary" base={base.snapshot.annualSalary} compare={compare.snapshot.annualSalary} />
                <TextRow label="Contract type" base={base.snapshot.contractType ?? null} compare={compare.snapshot.contractType ?? null} />
                <TextRow label="Start date" base={base.snapshot.startDate ?? null} compare={compare.snapshot.startDate ?? null} />
                <TextRow label="End date" base={base.snapshot.endDate ?? null} compare={compare.snapshot.endDate ?? null} />
                <TextRow label="Key terms" base={base.snapshot.keyTerms ?? null} compare={compare.snapshot.keyTerms ?? null} />
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
